import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import FloatingAIChat from '../../components/ui/FloatingAIChat';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import UserSelector from './components/UserSelector';
import CalendarSidebar from './components/CalendarSidebar';
import CalendarHeader from './components/CalendarHeader';
import CalendarGrid from './components/CalendarGrid';
import EventModal from './components/EventModal';
import AppointmentRequestModal from '../appointment-request-modal';
import { 
  createCalendarEvent, 
  updateCalendarEvent, 
  deleteCalendarEvent, 
  getCalendarEvents,
  getPublicCalendarEvents
} from '../../repos/calendarRepo';
import { convertFrontendToDB } from '../../repos/calendarTypeMapping';
import useToast from '../../hooks/useToast';
import Toast from '../../components/ui/Toast';

const CalendarMainInterface = () => {
  const { session } = useAuth();
  const isNotStudent = session?.userTenant?.role !== 'student';
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');
  const [selectedUser, setSelectedUser] = useState('my-calendar');
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [appointmentModalKey, setAppointmentModalKey] = useState(0);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const { showSuccess, showError, showWarning, toasts, removeToast } = useToast();
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // State for calendar events by user
  const [userEvents, setUserEvents] = useState({
    'my-calendar': [
      {
        id: 1,
        title: 'Mentoria com Maria Silva',
        type: 'atendimento',
        startTime: '2025-01-15 14:00',
        endTime: '2025-01-15 15:00',
        status: 'confirmed',
        attendees: ['maria.silva@email.com'],
        description: 'Discussão sobre estratégias de carreira',
        visibility: 'public'
      },
      {
        id: 2,
        title: 'Revisão de Projeto - João',
        type: 'atendimento',
        startTime: '2025-01-16 10:00',
        endTime: '2025-01-16 10:45',
        status: 'confirmed',
        attendees: ['joao.santos@email.com'],
        description: 'Feedback sobre projeto React',
        visibility: 'public'
      },
      {
        id: 3,
        title: 'Reunião Administrativa',
        type: 'bloqueado',
        startTime: '2025-01-17 09:00',
        endTime: '2025-01-17 10:30',
        status: 'busy',
        attendees: [],
        description: 'Reunião interna - não disponível',
        visibility: 'private'
      },
      {
        id: 4,
        title: 'Horário Disponível',
        type: 'disponivel',
        startTime: '2025-01-17 14:00',
        endTime: '2025-01-17 15:30',
        status: 'available',
        attendees: [],
        description: 'Slot disponível para agendamentos',
        visibility: 'public'
      },
      {
        id: 5,
        title: 'Workshop React Hooks',
        type: 'evento',
        startTime: '2025-01-18 19:00',
        endTime: '2025-01-18 21:00',
        status: 'confirmed',
        attendees: ['multiple'],
        description: 'Apresentação sobre React Hooks avançados',
        visibility: 'public'
      }
    ],
    'diane-calendar': [
      {
        id: 6,
        title: 'Consulta com Cliente A',
        type: 'atendimento',
        startTime: '2025-01-15 16:00',
        endTime: '2025-01-15 17:00',
        status: 'confirmed',
        attendees: ['cliente@email.com'],
        description: 'Análise de requisitos',
        visibility: 'public'
      },
      {
        id: 7,
        title: 'Reunião de Equipe',
        type: 'evento',
        startTime: '2025-01-16 14:00',
        endTime: '2025-01-16 15:00',
        status: 'confirmed',
        attendees: ['equipe@email.com'],
        description: 'Planejamento semanal',
        visibility: 'restricted'
      }
    ],
    'lindsey-calendar': [
      {
        id: 8,
        title: 'Treinamento Técnico',
        type: 'evento',
        startTime: '2025-01-17 10:00',
        endTime: '2025-01-17 12:00',
        status: 'confirmed',
        attendees: ['devs@email.com'],
        description: 'Workshop sobre novas tecnologias',
        visibility: 'public'
      }
    ]
  });

  const userCalendars = [
    { id: 'my-calendar', name: 'Meu calendário', color: '#4B1E1E', isOwn: true },
    { id: 'diane-calendar', name: 'Calendário da Diane', color: '#C89D4B', isOwn: false },
    { id: 'lindsey-calendar', name: 'Calendário da Lindsey', color: '#2E4E4E', isOwn: false }
  ];

  const handleDateNavigation = (direction) => {
    const newDate = new Date(currentDate);
    
    if (currentView === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else {
      newDate.setDate(newDate.getDate() + (direction * 7));
    }
    
    setCurrentDate(newDate);
  };

  const handleYearNavigation = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(newDate.getFullYear() + direction);
    setCurrentDate(newDate);
  };

  const handleEventClick = (event) => {
    console.log('🔍 CalendarMainInterface: evento clicado, abrindo modal:', event);
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleNewEvent = () => {
    setSelectedEvent(null);
    setShowNewEvent(true);
    setShowEventModal(true);
  };

  const handleRequestAppointment = () => {
    setShowAppointmentModal(true);
    setAppointmentModalKey(prev => prev + 1);
  };

  // Load events from database on component mount
  useEffect(() => {
    const loadEvents = async () => {
      if (!session?.systemUser?.id) return;
      
      try {
        setIsLoading(true);
        
        // Load own events
        const ownEvents = await getCalendarEvents(session.systemUser.id);
        console.log('🔍 CalendarMainInterface: eventos carregados do banco:', {
          ownEvents: ownEvents,
          ownEventsCount: ownEvents.length
        });
        
        const ownEventsFormatted = ownEvents.map(event => ({
          ...event,
          startTime: event.start_time,
          endTime: event.end_time,
          createdAt: event.created_at,
          updatedAt: event.updated_at
        }));
        
        // Load public events from other users (simplified for now)
        const otherUsersEvents = await getPublicCalendarEvents(session.systemUser.id);
        const otherEventsFormatted = otherUsersEvents.map(event => ({
          ...event,
          startTime: event.start_time,
          endTime: event.end_time,
          createdAt: event.created_at,
          updatedAt: event.updated_at
        }));
        
        const updatedUserEvents = {
          'my-calendar': ownEventsFormatted,
          'diane-calendar': otherEventsFormatted.filter(e => e.visibility === 'public'),
          'lindsey-calendar': otherEventsFormatted.filter(e => e.visibility === 'public')
        };
        
        console.log('🔍 CalendarMainInterface: userEvents atualizado:', updatedUserEvents);
        console.log('🔍 CalendarMainInterface: contagem por tipo:', {
          atendimentos: ownEventsFormatted.filter(e => e.type === 'atendimento').length,
          disponivel: ownEventsFormatted.filter(e => e.type === 'disponivel').length,
          bloqueado: ownEventsFormatted.filter(e => e.type === 'bloqueado').length,
          eventos: ownEventsFormatted.filter(e => e.type === 'evento').length
        });
        
        setUserEvents(updatedUserEvents);
      } catch (error) {
        console.error('❌ Erro ao carregar eventos:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadEvents();
  }, [session?.systemUser?.id]);

  const handleSaveEvent = async (eventData) => {
    if (!eventData || !session?.systemUser?.id) return;
    
    console.log('🔍 Dados recebidos para salvar:', eventData);
    
    try {
      if (eventData.id && !showNewEvent) {
        // É uma edição - atualizar evento existente
        console.log('🔍 CalendarMainInterface: editando evento existente com dados:', {
          id: eventData.id,
          type: eventData.type,
          title: eventData.title
        });
        
        const updatedEvent = await updateCalendarEvent(eventData.id, {
          title: eventData.title,
          type: eventData.type,
          startTime: eventData.startTime,
          endTime: eventData.endTime,
          description: eventData.description,
          visibility: eventData.visibility,
          attendees: eventData.attendees
        });
        
        // Update local state
        console.log('🔍 CalendarMainInterface: evento atualizado, dados retornados:', updatedEvent);
        
        setUserEvents(prev => {
          const updatedEvents = {
            ...prev,
            [selectedUser]: prev[selectedUser].map(event => 
              event.id === eventData.id ? { 
                ...event, 
                ...updatedEvent
                // Não precisamos mapear campos pois updatedEvent já vem convertido
              } : event
            )
          };
          
          console.log('🔍 CalendarMainInterface: estado atualizado após edição:', updatedEvents);
          console.log('🔍 CalendarMainInterface: contagem após edição:', {
            total: updatedEvents[selectedUser].length,
            atendimentos: updatedEvents[selectedUser].filter(e => e.type === 'atendimento').length,
            disponivel: updatedEvents[selectedUser].filter(e => e.type === 'disponivel').length,
            bloqueado: updatedEvents[selectedUser].filter(e => e.type === 'bloqueado').length,
            eventos: updatedEvents[selectedUser].filter(e => e.type === 'evento').length
          });
          
          return updatedEvents;
        });
      } else {
        // É um novo evento - criar novo
        console.log('🔍 CalendarMainInterface: criando novo evento com dados:', eventData);
        
        const newEvent = await createCalendarEvent({
          user_id: session.systemUser.id,
          title: eventData.title,
          type: eventData.type,
          startTime: eventData.startTime,
          endTime: eventData.endTime,
          status: 'confirmed',
          description: eventData.description,
          visibility: eventData.visibility,
          attendees: eventData.attendees
        });
        
        console.log('🔍 CalendarMainInterface: novo evento retornado pelo banco:', newEvent);
        
        // Update local state
        console.log('🔍 CalendarMainInterface: novo evento criado, atualizando estado:', newEvent);
        
        setUserEvents(prev => {
          console.log('🔍 CalendarMainInterface: estado anterior antes da atualização:', prev);
          console.log('🔍 CalendarMainInterface: calendário selecionado:', selectedUser);
          console.log('🔍 CalendarMainInterface: eventos existentes no calendário:', prev[selectedUser]);
          
          const updatedEvents = {
            ...prev,
            [selectedUser]: [...(prev[selectedUser] || []), {
              ...newEvent,
              startTime: newEvent.start_time,
              endTime: newEvent.end_time,
              createdAt: newEvent.created_at,
              updatedAt: newEvent.updated_at
            }]
          };
          
          console.log('🔍 CalendarMainInterface: estado atualizado para novos eventos:', updatedEvents);
          console.log('🔍 CalendarMainInterface: contagem após adição:', {
            total: updatedEvents[selectedUser].length,
            atendimentos: updatedEvents[selectedUser].filter(e => e.type === 'atendimento').length,
            disponivel: updatedEvents[selectedUser].filter(e => e.type === 'disponivel').length,
            bloqueado: updatedEvents[selectedUser].filter(e => e.type === 'bloqueado').length,
            eventos: updatedEvents[selectedUser].filter(e => e.type === 'evento').length
          });
          
                    return updatedEvents;
        });
        
        // Verificar se o estado foi atualizado corretamente
        setTimeout(() => {
          console.log('🔍 CalendarMainInterface: verificação pós-atualização do estado:', userEvents);
        }, 100);
      }
    } catch (error) {
        console.error('❌ Erro ao salvar evento:', error);
        showError('Erro ao salvar evento. Tente novamente.');
        return;
      }
    
    setShowEventModal(false);
    setSelectedEvent(null);
    setShowNewEvent(false);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Tem certeza que deseja excluir este evento?')) return;
    
    try {
      await deleteCalendarEvent(eventId);
      
      // Update local state
      setUserEvents(prev => ({
        ...prev,
        [selectedUser]: prev[selectedUser].filter(event => event.id !== eventId)
      }));
      
      setShowEventModal(false);
      setSelectedEvent(null);
      setShowNewEvent(false);
    } catch (error) {
      console.error('❌ Erro ao deletar evento:', error);
      showError('Erro ao deletar evento. Tente novamente.');
    }
  };

  // Memoized values
  const getCurrentCalendar = useMemo(() => {
    return userCalendars?.find(cal => cal?.id === selectedUser) || userCalendars?.[0] || null;
  }, [userCalendars, selectedUser]);

  const filteredEvents = useMemo(() => {
    const currentUserEvents = userEvents[selectedUser] || [];
    
    if (selectedUser === 'my-calendar') {
      return currentUserEvents; // Show all own events
    }
    
    // For other users, show public events or mark private/restricted as "occupied"
    return currentUserEvents.map(event => {
      if (event.visibility === 'private' || event.visibility === 'restricted') {
        return { 
          ...event, 
          title: '🔒 Privado', 
          type: 'bloqueado',
          description: 'Evento privado ou restrito',
          attendees: []
        };
      }
      return event;
    });
  }, [userEvents, selectedUser]);

  const breadcrumbItems = useMemo(() => [
    { label: 'Início', path: '/dashboard-home' },
    { label: 'Calendário', path: '/calendar-main-interface', isActive: true }
  ], []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className={`pt-16 transition-all duration-300`}>
        <div className="flex h-[calc(100vh-4rem)]">
          {/* Calendar Sidebar */}
          <CalendarSidebar 
            userCalendars={userCalendars}
            selectedUser={selectedUser}
            onUserSelect={setSelectedUser}
            onNewEvent={handleNewEvent}
            onRequestAppointment={handleRequestAppointment}
            calendarEvents={userEvents[selectedUser] || []}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            isNotStudent={isNotStudent}
          />
          
          {/* Debug: Verificar dados sendo passados para o sidebar */}
          {console.log('🔍 CalendarMainInterface: dados sendo passados para sidebar:', {
            selectedUser,
            calendarEvents: userEvents[selectedUser] || [],
            totalEvents: userEvents[selectedUser]?.length || 0
          })}

          {/* Main Calendar Area */}
          <div className={`flex-1 flex flex-col transition-all duration-300 ${
            isSidebarCollapsed ? 'ml-16' : 'ml-0'
          }`}>
            <div className="p-6 pb-0">
              <Breadcrumb customItems={breadcrumbItems} />
              
              {/* Page Header */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                    Calendário
                  </h1>
                  <p className="text-muted-foreground font-caption">
                    Gerencie seus agendamentos e disponibilidade
                  </p>
                </div>
                
                <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                  <UserSelector 
                    calendars={userCalendars}
                    selectedUser={selectedUser}
                    onUserSelect={setSelectedUser}
                  />
                  {getCurrentCalendar?.isOwn && (
                    <>
                      <Button 
                        variant="default" 
                        iconName="Plus" 
                        iconPosition="left" 
                        iconSize={16}
                        onClick={handleNewEvent}
                      >
                        Novo Agendamento
                      </Button>
                      <Button 
                        variant="secondary" 
                        iconName="MessageSquare" 
                        iconPosition="left" 
                        iconSize={16}
                        onClick={handleRequestAppointment}
                      >
                        Solicitar Atendimento
                      </Button>
                    </>
                  )}
                  {!getCurrentCalendar?.isOwn && (
                    <Button 
                      variant="secondary" 
                      iconName="MessageSquare" 
                      iconPosition="left" 
                      iconSize={16}
                      onClick={handleRequestAppointment}
                    >
                      Solicitar Atendimento
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Calendar Header Controls */}
            <div className="px-6 pb-4">
              <CalendarHeader
                currentDate={currentDate}
                currentView={currentView}
                onViewChange={setCurrentView}
                onDateNavigation={handleDateNavigation}
                onYearNavigation={handleYearNavigation}
                onToday={() => setCurrentDate(new Date())}
              />
            </div>

            {/* Calendar Grid */}
            <div className="flex-1 px-6 pb-6 calendar-container">
              {/* FullCalendar.js integration placeholder */}
              <div className="bg-card border border-border rounded-lg shadow-tier-1 h-full">
                <CalendarGrid
                  currentDate={currentDate}
                  currentView={currentView}
                  events={filteredEvents}
                  onEventClick={handleEventClick}
                  onDateClick={(date) => {}}
                  selectedCalendar={getCurrentCalendar}
                />
              </div>
              
              {/* FullCalendar.js integration comment */}
              {/* <script src="fullcalendar.js"></script> */}
            </div>
          </div>
        </div>

        {/* Event Modal */}
        {showEventModal && (
          <EventModal
            event={selectedEvent}
            isNew={showNewEvent}
            onClose={() => {
              console.log('🔍 CalendarMainInterface: fechando modal, limpando estado');
              setShowEventModal(false);
              setSelectedEvent(null);
              setShowNewEvent(false);
            }}
            onSave={handleSaveEvent}
            onDelete={handleDeleteEvent}
            canEdit={getCurrentCalendar?.isOwn}
          />
        )}

        {/* Appointment Request Modal */}
        {showAppointmentModal && (
          <AppointmentRequestModal 
            key={appointmentModalKey}
            isOpen={showAppointmentModal}
            onClose={() => setShowAppointmentModal(false)}
          />
        )}
      </main>
      
      {/* Toast notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
      
      <FloatingAIChat />
    </div>
  );
};

export default CalendarMainInterface;